/**
 * InsolationController — time slider + interactive legend for Insolation mode.
 *
 * Layout (top → bottom):
 *   [−10 min]   12:00   [+10 min]
 *   [════════ slider ════════════]    (step = 1 min)
 *     06:00     12:00     18:00      (clickable — snap to that hour)
 *   ─────────────────────────────
 *   zaznaczono tylko / SALON Z ANEKSEM [×]   (only when focused)
 *   ─────────────────────────────
 *   [legend: 0h+ · 1h+ · … · 10h]
 *
 * Slider step is 1 minute (0.5°). The ±10 min buttons snap to the nearest
 * 10-minute grid (so 12:04 → 12:10, 12:08 → 12:20) and hold-to-repeat.
 */

import { useMemo, useCallback, useRef, useEffect } from 'react'

import { useI18n } from '../../../../shared/i18n/useI18n.js'
import { Slider } from '../../../../shared/ui/Slider/index.js'
import { Tooltip } from '../../../../shared/ui/Tooltip/index.js'
import { sumSunLightRanges } from '../../../../shared/utils/sunLightUtils.js'

import { useProduct } from '../../index.js'
import { INSOLATION_DEFAULTS } from '../../constants/overlayDefaults.js'
import { angleToTime, hourToAngle } from '../../utils/svgInsolation.js'

import styles from './InsolationController.module.scss'

// ── Constants ─────────────────────────────────────────────────────────────────

// 12h span (06:00→18:00) = 180° → 1 min = 0.5°.
const DEG_PER_MIN     = 0.5
const STEP_10MIN_DEG  = 10 * DEG_PER_MIN   // 5°
const SLIDER_STEP_DEG = 1  * DEG_PER_MIN   // 0.5°

// Hold-to-repeat timings for the ±10 min buttons.
const HOLD_INITIAL_DELAY_MS = 350
const HOLD_INTERVAL_MS      = 110

// Buckets 0–10 matching --bim-color-insolation-{n} CSS vars.
const BUCKETS = Array.from({ length: 11 }, (_, i) => i)

// Hour markers under the slider — clickable.
const HOUR_MARKERS = [6, 12, 18]

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Snap an angle to the nearest 10-minute grid (5° increments anchored on
 * 06:00 = 90°). Clamped to the insolation domain.
 */
function snapToTenMinutes(angle) {
  const { minAngleDeg, maxAngleDeg } = INSOLATION_DEFAULTS
  const snapped = Math.round((angle - minAngleDeg) / STEP_10MIN_DEG) * STEP_10MIN_DEG + minAngleDeg
  return Math.min(maxAngleDeg, Math.max(minAngleDeg, snapped))
}

// ── Legend ────────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {Set<number>} props.activeHours  — hour buckets present in this apartment
 * @param {number|null} props.hoveredBucket — active bucket (from legend hover OR path hover)
 * @param {(h: number|null) => void} props.onHover
 */
function InsolationLegend({ activeHours, hoveredBucket, onHover }) {
  const { t } = useI18n()

  return (
    <div className={styles.legend}>
      {BUCKETS.map((h) => {
        const isActive      = activeHours.has(h)
        const isHighlighted = hoveredBucket === h
        const isDimmed      = hoveredBucket != null && !isHighlighted
        const label = h < 10 ? `${h}h+` : `${h}h`
        const tooltipText = t(`product.insolation_legend_tooltip_${h}`, '')

        return (
          <Tooltip
            key={h}
            title={tooltipText || null}
            placement="top"
            disabled={!tooltipText}
          >
            <div
              className={[
                styles.legendItem,
                isActive      ? styles.legendItemActive  : '',
                isHighlighted ? styles.legendItemHovered : '',
              ].filter(Boolean).join(' ')}
              onMouseEnter={() => onHover(h)}
              onMouseLeave={() => onHover(null)}
            >
              <span
                className={styles.legendSwatch}
                style={{
                  background: isDimmed
                    ? `var(--bim-color-insolation-${h}-muted)`
                    : `var(--bim-color-insolation-${h})`,
                }}
              />
              <span className={styles.legendLabel}>{label}</span>
            </div>
          </Tooltip>
        )
      })}
    </div>
  )
}

// ── InsolationController ──────────────────────────────────────────────────────

export function InsolationController() {
  const { t } = useI18n()
  const { selection, viewerState, viewerActions } = useProduct()
  const {
    insolationAngleDeg,
    insolationFocusedCompartmentId,
    insolationHoveredHour,
    insolationHoveredPathBucket,
  } = viewerState
  const {
    setInsolationAngleDeg,
    setInsolationFocusedCompartmentId,
    setInsolationHoveredHour,
  } = viewerActions

  const time = angleToTime(insolationAngleDeg)

  const productCompartments = useMemo(
    () => (Array.isArray(selection.product?.compartments) ? selection.product.compartments : []),
    [selection.product],
  )

  // Buckets that actually appear in this apartment (for legend highlighting).
  const activeHours = useMemo(() => {
    const set = new Set()
    for (const c of productCompartments) {
      if (!c?.window_area || Number(c.window_area) <= 0) continue
      const h = Math.min(10, Math.max(0, Math.floor(sumSunLightRanges(c.sun_light_ranges))))
      set.add(h)
    }
    return set
  }, [productCompartments])

  const focusedCompartment = useMemo(() => {
    if (insolationFocusedCompartmentId == null) return null
    return productCompartments.find((c) => Number(c?.number) === Number(insolationFocusedCompartmentId)) ?? null
  }, [insolationFocusedCompartmentId, productCompartments])

  const focusedName = focusedCompartment?.name_cad
    ? t(`compartments.${focusedCompartment.name_cad}`, focusedCompartment.name_cad)
    : null

  // ── ±10 min buttons — single click snaps, held button repeats ──────────────

  // Keep latest angle in a ref so the interval callback always reads fresh.
  const angleRef = useRef(insolationAngleDeg)
  useEffect(() => { angleRef.current = insolationAngleDeg }, [insolationAngleDeg])

  // Single step: from CURRENT angle, add ±10 min and round to nearest 10 grid.
  // Example: 12:04 (+10) → round(14 min / 10) * 10 = 10 → 12:10
  //          12:08 (+10) → round(18 min / 10) * 10 = 20 → 12:20
  const stepBy10Min = useCallback((sign) => {
    const next = snapToTenMinutes(angleRef.current + sign * STEP_10MIN_DEG)
    if (next !== angleRef.current) setInsolationAngleDeg(next)
  }, [setInsolationAngleDeg])

  // Hold-to-repeat: pointerdown → immediate step + start interval; release → stop.
  const holdTimerRef    = useRef(null)
  const holdIntervalRef = useRef(null)

  const stopHold = useCallback(() => {
    if (holdTimerRef.current)    { clearTimeout (holdTimerRef.current);    holdTimerRef.current    = null }
    if (holdIntervalRef.current) { clearInterval(holdIntervalRef.current); holdIntervalRef.current = null }
  }, [])

  // Cleanup any pending timers on unmount.
  useEffect(() => () => stopHold(), [stopHold])

  const startHold = useCallback((sign) => (e) => {
    // Only left mouse / touch — ignore right-click context menu.
    if (e.button != null && e.button !== 0) return
    e.preventDefault()
    stopHold()
    stepBy10Min(sign)
    holdTimerRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => stepBy10Min(sign), HOLD_INTERVAL_MS)
    }, HOLD_INITIAL_DELAY_MS)
  }, [stepBy10Min, stopHold])

  // ── Hour-marker click → set that hour ──────────────────────────────────────

  const setHour = useCallback((hour) => {
    setInsolationAngleDeg(hourToAngle(hour))
  }, [setInsolationAngleDeg])

  return (
    <div className={styles.controller}>

      {/* ── Time row ────────────────────────────────────────────────────── */}
      <div className={styles.timeRow}>
        <button
          type="button"
          className={styles.stepBtn}
          onMouseDown={startHold(-1)}
          onMouseUp={stopHold}
          onMouseLeave={stopHold}
          onTouchStart={startHold(-1)}
          onTouchEnd={stopHold}
          onTouchCancel={stopHold}
          onContextMenu={(e) => e.preventDefault()}
          aria-label="-10 min"
        >
          −10<br /><span className={styles.stepUnit}>min</span>
        </button>

        <strong className={styles.timeValue}>{time}</strong>

        <button
          type="button"
          className={styles.stepBtn}
          onMouseDown={startHold(+1)}
          onMouseUp={stopHold}
          onMouseLeave={stopHold}
          onTouchStart={startHold(+1)}
          onTouchEnd={stopHold}
          onTouchCancel={stopHold}
          onContextMenu={(e) => e.preventDefault()}
          aria-label="+10 min"
        >
          +10<br /><span className={styles.stepUnit}>min</span>
        </button>
      </div>

      {/* ── Slider (1-minute granularity) ───────────────────────────────── */}
      <div className={styles.sliderWrap}>
        <Slider
          value={insolationAngleDeg}
          defaultValue={[INSOLATION_DEFAULTS.minAngleDeg, INSOLATION_DEFAULTS.maxAngleDeg]}
          step={SLIDER_STEP_DEG}
          onChange={setInsolationAngleDeg}
          formatValue={angleToTime}
          ariaLabel={t('product.insolation_time_label')}
        />
        <div className={styles.hourMarkers}>
          {HOUR_MARKERS.map((h) => (
            <button
              key={h}
              type="button"
              className={styles.hourMarker}
              onClick={() => setHour(h)}
              aria-label={`${String(h).padStart(2, '0')}:00`}
            >
              {String(h).padStart(2, '0')}:00
            </button>
          ))}
        </div>
      </div>

      {/* ── Focused compartment strip ────────────────────────────────────── */}
      {focusedCompartment && (
        <div className={styles.focused}>
          <span className={styles.focusedMeta}>{t('product.insolation_focused_only')}</span>
          <span className={styles.focusedName}>
            {focusedName ?? `#${insolationFocusedCompartmentId}`}
          </span>
          <button
            type="button"
            className={styles.focusedReset}
            onClick={() => setInsolationFocusedCompartmentId(null)}
            aria-label={t('product.insolation_focus_clear')}
          >
            ×
          </button>
        </div>
      )}

      {/* ── Legend ──────────────────────────────────────────────────────── */}
      <InsolationLegend
        activeHours={activeHours}
        hoveredBucket={insolationHoveredHour ?? insolationHoveredPathBucket}
        onHover={setInsolationHoveredHour}
      />

    </div>
  )
}
